import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Search, BookOpen, HelpCircle, ChevronRight, ArrowLeft, 
  Rocket, ClipboardCheck, AlertTriangle, Cpu, FileText,
  GraduationCap, Smartphone, Settings, ExternalLink, Home,
  CheckCircle, Info, Lightbulb, Play, RefreshCw, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for categories
const CATEGORY_ICONS = {
  'rocket': Rocket,
  'clipboard-check': ClipboardCheck,
  'alert-triangle': AlertTriangle,
  'cpu': Cpu,
  'file-text': FileText,
  'graduation-cap': GraduationCap,
  'smartphone': Smartphone,
  'settings': Settings
};

const HelpCenterPage = () => {
  const navigate = useNavigate();
  const { articleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [suggestedArticles, setSuggestedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/help/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load help categories', variant: 'destructive' });
    }
  }, [toast]);

  const fetchArticles = useCallback(async (category = null) => {
    try {
      const url = category ? `${API}/help/articles?category=${category}` : `${API}/help/articles`;
      const response = await axios.get(url);
      setArticles(response.data.articles || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load articles', variant: 'destructive' });
    }
  }, [toast]);

  const fetchArticle = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API}/help/articles/${id}`);
      setSelectedArticle(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Article not found', variant: 'destructive' });
      setSelectedArticle(null);
    }
  }, [toast]);

  const fetchSuggested = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/help/suggested`);
      setSuggestedArticles(response.data.articles || []);
    } catch (error) {
      // Silently fail
    }
  }, []);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await axios.get(`${API}/help/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Search failed', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  }, [searchQuery, toast]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchCategories(), fetchArticles(), fetchSuggested()]);
      setLoading(false);
    };
    init();
  }, [fetchCategories, fetchArticles, fetchSuggested]);

  useEffect(() => {
    if (articleId) {
      fetchArticle(articleId);
    } else {
      setSelectedArticle(null);
    }
  }, [articleId, fetchArticle]);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
      fetchArticles(category);
    }
  }, [searchParams, fetchArticles]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery, handleSearch]);

  const selectCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchParams({ category: categoryId });
    fetchArticles(categoryId);
    setSelectedArticle(null);
    navigate('/help');
  };

  const selectArticle = (articleId) => {
    navigate(`/help/${articleId}`);
  };

  const goHome = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
    setSearchQuery('');
    setSearchResults([]);
    setSearchParams({});
    fetchArticles();
    navigate('/help');
  };

  const getCategoryIcon = (iconName) => {
    const IconComponent = CATEGORY_ICONS[iconName] || HelpCircle;
    return IconComponent;
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      'getting-started': 'from-blue-500 to-indigo-600',
      'safety-audits': 'from-green-500 to-emerald-600',
      'incident-management': 'from-red-500 to-rose-600',
      'ai-tools': 'from-purple-500 to-violet-600',
      'documentation': 'from-amber-500 to-orange-600',
      'training': 'from-cyan-500 to-teal-600',
      'field-mode': 'from-pink-500 to-fuchsia-600',
      'admin': 'from-slate-500 to-gray-600'
    };
    return colors[categoryId] || 'from-gray-500 to-slate-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Article Detail View
  if (selectedArticle) {
    const category = categories.find(c => c.id === selectedArticle.category);
    const CategoryIcon = getCategoryIcon(category?.icon);

    return (
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <button onClick={goHome} className="text-blue-600 hover:underline flex items-center">
            <Home className="w-4 h-4 mr-1" /> Help Center
          </button>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <button 
            onClick={() => selectCategory(selectedArticle.category)} 
            className="text-blue-600 hover:underline"
          >
            {category?.name}
          </button>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-600">{selectedArticle.title}</span>
        </div>

        {/* Article Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCategoryColor(selectedArticle.category)} flex items-center justify-center`}>
              <CategoryIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{selectedArticle.title}</h1>
              <p className="text-gray-600">{selectedArticle.summary}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedArticle.tags?.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="bg-gray-50">
                {tag}
              </Badge>
            ))}
          </div>
        </div>

        {/* Hero Image */}
        {selectedArticle.hero_image && (
          <Card className="mb-8 rounded-2xl overflow-hidden">
            <img 
              src={selectedArticle.hero_image} 
              alt={selectedArticle.title}
              className="w-full h-auto max-h-96 object-cover"
            />
          </Card>
        )}

        {/* Steps (if available) */}
        {selectedArticle.steps?.length > 0 && (
          <Card className="mb-8 rounded-2xl border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-800">
                <Play className="w-5 h-5 mr-2" />
                Quick Steps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {selectedArticle.steps.map((step, idx) => (
                  <div key={idx} className="space-y-3">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{step.title}</h4>
                        <p className="text-gray-600 text-sm">{step.description}</p>
                      </div>
                    </div>
                    {step.image && (
                      <div className="ml-12 rounded-xl overflow-hidden border shadow-sm">
                        <img 
                          src={step.image} 
                          alt={`Step ${step.step}: ${step.title}`}
                          className="w-full h-auto max-h-64 object-cover"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Article Content */}
        <Card className="rounded-2xl">
          <CardContent className="p-8 prose prose-slate max-w-none">
            <ReactMarkdown
              components={{
                h1: ({children}) => <h1 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{children}</h1>,
                h2: ({children}) => <h2 className="text-xl font-semibold text-gray-800 mt-6 mb-3">{children}</h2>,
                h3: ({children}) => <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">{children}</h3>,
                p: ({children}) => <p className="text-gray-600 mb-4 leading-relaxed">{children}</p>,
                ul: ({children}) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
                li: ({children}) => <li className="text-gray-600">{children}</li>,
                strong: ({children}) => <strong className="font-semibold text-gray-800">{children}</strong>,
                code: ({children}) => <code className="bg-gray-100 px-2 py-1 rounded text-sm">{children}</code>,
                img: ({src, alt}) => (
                  <div className="my-6 rounded-xl overflow-hidden shadow-lg border">
                    <img src={src} alt={alt || 'Help illustration'} className="w-full h-auto" />
                    {alt && <p className="text-center text-sm text-gray-500 py-2 bg-gray-50">{alt}</p>}
                  </div>
                )
              }}
            >
              {selectedArticle.content}
            </ReactMarkdown>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between">
          <Button variant="outline" onClick={() => selectCategory(selectedArticle.category)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {category?.name}
          </Button>
          <Button variant="outline" onClick={goHome}>
            <Home className="w-4 h-4 mr-2" />
            Help Center Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl text-white">
        <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-90" />
        <h1 className="text-4xl font-bold mb-2">Help Center</h1>
        <p className="text-blue-100 text-lg mb-8 max-w-xl mx-auto">
          Learn how to use every feature in WSafe AI with step-by-step guides and tutorials
        </p>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto relative">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for help... (e.g., 'how to create audit', 'AI scan')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-12 py-6 text-lg rounded-2xl text-gray-900 bg-white border-0 shadow-xl"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {searching && (
            <div className="absolute inset-x-0 top-full mt-2 bg-white rounded-xl shadow-lg p-4 text-gray-600">
              <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />
              Searching with AI...
            </div>
          )}
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="w-5 h-5 mr-2 text-blue-600" />
              Search Results for "{searchQuery}"
            </CardTitle>
            <CardDescription>{searchResults.length} article(s) found</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searchResults.map((article) => (
                <div
                  key={article.id}
                  onClick={() => selectArticle(article.id)}
                  className="p-4 bg-gray-50 rounded-xl hover:bg-blue-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900 group-hover:text-blue-600">
                        {article.title}
                      </h4>
                      <p className="text-sm text-gray-500">{article.summary}</p>
                      <div className="flex gap-2 mt-2">
                        {article.tags?.slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Categories */}
      {!searchQuery && (
        <>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Browse by Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {categories.map((category) => {
                const CategoryIcon = getCategoryIcon(category.icon);
                return (
                  <Card
                    key={category.id}
                    onClick={() => selectCategory(category.id)}
                    className={`rounded-2xl cursor-pointer hover:shadow-lg transition-all group overflow-hidden ${
                      selectedCategory === category.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <div className={`h-2 bg-gradient-to-r ${getCategoryColor(category.id)}`}></div>
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getCategoryColor(category.id)} flex items-center justify-center mb-4`}>
                        <CategoryIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 mb-1">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-500">{category.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Category Articles */}
          {selectedCategory && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {categories.find(c => c.id === selectedCategory)?.name}
                </h2>
                <Button variant="outline" onClick={goHome}>
                  <X className="w-4 h-4 mr-2" /> Clear Filter
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {articles.map((article) => (
                  <Card
                    key={article.id}
                    onClick={() => selectArticle(article.id)}
                    className="rounded-xl cursor-pointer hover:shadow-md transition-all group"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 mb-1">
                            {article.title}
                          </h4>
                          <p className="text-sm text-gray-500 mb-3">{article.summary}</p>
                          <div className="flex items-center gap-2">
                            {article.has_steps && (
                              <Badge className="bg-blue-100 text-blue-700">
                                <Play className="w-3 h-3 mr-1" /> Step-by-step
                              </Badge>
                            )}
                            {article.tags?.slice(0, 2).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Articles */}
          {!selectedCategory && suggestedArticles.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Lightbulb className="w-6 h-6 mr-2 text-amber-500" />
                Popular Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {suggestedArticles.map((article) => {
                  const category = categories.find(c => c.id === article.category);
                  const CategoryIcon = getCategoryIcon(category?.icon);
                  return (
                    <Card
                      key={article.id}
                      onClick={() => selectArticle(article.id)}
                      className="rounded-xl cursor-pointer hover:shadow-md transition-all group"
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getCategoryColor(article.category)} flex items-center justify-center flex-shrink-0`}>
                            <CategoryIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 group-hover:text-blue-600">
                              {article.title}
                            </h4>
                            <p className="text-sm text-gray-500">{article.summary}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Articles (when no category selected) */}
          {!selectedCategory && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">All Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {articles.map((article) => {
                  const category = categories.find(c => c.id === article.category);
                  return (
                    <Card
                      key={article.id}
                      onClick={() => selectArticle(article.id)}
                      className="rounded-xl cursor-pointer hover:shadow-md transition-all group"
                    >
                      <CardContent className="p-4">
                        <Badge variant="outline" className="mb-2 text-xs">
                          {category?.name}
                        </Badge>
                        <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 mb-1">
                          {article.title}
                        </h4>
                        <p className="text-sm text-gray-500 line-clamp-2">{article.summary}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Quick Help */}
      <Card className="rounded-2xl bg-gradient-to-br from-gray-50 to-slate-100">
        <CardContent className="p-8 text-center">
          <HelpCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Still need help?</h3>
          <p className="text-gray-600 mb-4">
            Can't find what you're looking for? Contact our support team.
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline">
              Contact Support
            </Button>
            <Button>
              <ExternalLink className="w-4 h-4 mr-2" />
              Video Tutorials
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpCenterPage;
